import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Plus, 
  Calendar, 
  Users, 
  CheckCircle2, 
  ArrowRight,
  MoreVertical,
  Trash2,
  Search,
  Lock
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { currencySymbols } from '@/types';
import type { Meeting } from '@/types';

export default function Attendance() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentOrganization, setMeetings, meetings, addMeeting } = useAppStore();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Search / filter state
  const [searchText, setSearchText] = useState('');
  const [searchDate, setSearchDate] = useState('');
  
  // Form state
  const [meetingDate, setMeetingDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [meetingType, setMeetingType] = useState<'Regular' | 'Special' | 'Event'>('Regular');
  const [openingCash, setOpeningCash] = useState('');
  const [notes, setNotes] = useState('');

  const currencySymbol = currentOrganization?.currency 
    ? currencySymbols[currentOrganization.currency as keyof typeof currencySymbols] || '$'
    : '$';

  const filteredMeetings = useMemo(() => {
    return meetings.filter((meeting) => {
      const textMatch = searchText.trim() === '' ||
        meeting.meeting_type.toLowerCase().includes(searchText.toLowerCase()) ||
        (meeting.notes ?? '').toLowerCase().includes(searchText.toLowerCase());
      const dateMatch = searchDate === '' || meeting.meeting_date === searchDate;
      return textMatch && dateMatch;
    });
  }, [meetings, searchText, searchDate]);

  const hasFilter = searchText.trim() !== '' || searchDate !== '';

  useEffect(() => {
    fetchMeetings();
  }, [currentOrganization]);

  const fetchMeetings = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('meetings')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('meeting_date', { ascending: false });

      if (error) throw error;

      const meetings = data || [];

      // Fetch total active member count once (same value for every meeting)
      const { count: totalMembers } = await supabase
        .from('members')
        .select('*', { count: 'exact', head: true })
        .eq('organization_id', currentOrganization.id)
        .eq('status', 'active');

      // Fetch all present attendance records for all meetings in one query
      const meetingIds = meetings.map((m) => m.id);
      const attendanceCountMap = new Map<string, number>();
      if (meetingIds.length > 0) {
        const { data: attendanceRows } = await supabase
          .from('attendance')
          .select('meeting_id')
          .in('meeting_id', meetingIds)
          .eq('is_present', true);

        for (const row of attendanceRows || []) {
          attendanceCountMap.set(row.meeting_id, (attendanceCountMap.get(row.meeting_id) || 0) + 1);
        }
      }

      const meetingsWithCounts = meetings.map((meeting) => ({
        ...meeting,
        attendance_count: attendanceCountMap.get(meeting.id) || 0,
        total_members: totalMembers || 0,
      }));

      setMeetings(meetingsWithCounts);
    } catch (error) {
      console.error('Error fetching meetings:', error);
      toast.error(t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentOrganization) return;

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase
        .from('meetings')
        .insert({
          organization_id: currentOrganization.id,
          meeting_date: meetingDate,
          meeting_type: meetingType,
          opening_cash: parseFloat(openingCash) || 0,
          notes: notes || null,
        })
        .select()
        .single();

      if (error) throw error;

      // Initialize attendance records for all active members
      const { data: members, error: membersError } = await supabase
        .from('members')
        .select('id')
        .eq('organization_id', currentOrganization.id)
        .eq('status', 'active');

      if (membersError) throw membersError;

      // FIX #1: Check for active members before creating attendance
      if (members && members.length > 0) {
        const attendanceRecords = members.map((member) => ({
          meeting_id: data.id,
          member_id: member.id,
          is_present: false,
        }));

        // FIX #1: Improved error handling for attendance batch insert
        const { error: attendanceError } = await supabase
          .from('attendance')
          .insert(attendanceRecords);

        if (attendanceError) {
          console.error('Error creating attendance records:', attendanceError);
          toast.error('Meeting created but failed to initialize attendance records. Please try again.');
          throw attendanceError;
        }
      } else {
        // FIX #1: Warning for no active members
        toast.warning('Meeting created with no active members to track attendance');
      }

      addMeeting({ ...data, attendance_count: 0, total_members: members?.length || 0 });
      
      // FIX #1: Refresh meetings list after creation
      await fetchMeetings();

      toast.success('Meeting created successfully');
      setIsAddDialogOpen(false);
      
      // Navigate to attendance taking page
      navigate(`/attendance/${data.id}`);
    } catch (error) {
      console.error('Error creating meeting:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMeeting = async (meeting: Meeting) => {
    if (!confirm('Are you sure you want to delete this meeting?')) return;

    try {
      const { error } = await supabase
        .from('meetings')
        .delete()
        .eq('id', meeting.id);

      if (error) throw error;

      // Refresh meetings
      fetchMeetings();
      toast.success('Meeting deleted successfully');
    } catch (error) {
      console.error('Error deleting meeting:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    }
  };

  const today = format(new Date(), 'yyyy-MM-dd');

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('navigation.attendance')}</h1>
          <p className="text-muted-foreground">
            {hasFilter
              ? `${filteredMeetings.length} of ${meetings.length} ${meetings.length === 1 ? 'meeting' : 'meetings'}`
              : `${meetings.length} ${meetings.length === 1 ? 'meeting' : 'meetings'} recorded`}
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('attendance.createMeeting')}
        </Button>
      </div>

      {/* Search toolbar */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-9"
            placeholder="Search by meeting type or notes…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <Input
            className="pl-9 w-full sm:w-44"
            type="date"
            value={searchDate}
            onChange={(e) => setSearchDate(e.target.value)}
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setSearchText(''); setSearchDate(''); }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Meetings List */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : filteredMeetings.length > 0 ? (
        <div className="space-y-4">
          {filteredMeetings.map((meeting) => {
            const isFutureMeeting = meeting.meeting_date > today;
            return (
              <Card key={meeting.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div 
                      className={cn("flex-1", isFutureMeeting ? "cursor-default" : "cursor-pointer")}
                      onClick={() => !isFutureMeeting && navigate(`/attendance/${meeting.id}`)}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">
                          {meeting.meeting_type} Meeting
                        </h3>
                        <Badge variant="outline">
                          {format(new Date(meeting.meeting_date), 'MMM dd, yyyy')}
                        </Badge>
                        {isFutureMeeting && (
                          <Badge variant="secondary" className="gap-1">
                            <Lock className="w-3 h-3" />
                            Upcoming
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          <span>
                            {meeting.attendance_count}/{meeting.total_members} present
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          <span>
                            {(meeting.total_members || 0) > 0 
                              ? Math.round(((meeting.attendance_count || 0) / (meeting.total_members || 1)) * 100) 
                              : 0}% attendance
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>Opening: {currencySymbol}{meeting.opening_cash}</span>
                        </div>
                      </div>
                      
                      {meeting.notes && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-1">
                          {meeting.notes}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        disabled={isFutureMeeting}
                        title={isFutureMeeting ? `Attendance opens on ${format(new Date(meeting.meeting_date), 'MMM dd, yyyy')}` : undefined}
                        onClick={() => !isFutureMeeting && navigate(`/attendance/${meeting.id}`)}
                      >
                        {isFutureMeeting ? (
                          <>
                            <Lock className="w-4 h-4 mr-2" />
                            Not Open Yet
                          </>
                        ) : (
                          <>
                            Take Attendance
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </>
                        )}
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleDeleteMeeting(meeting)}>
                            <Trash2 className="w-4 h-4 mr-2 text-red-500" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          {hasFilter ? (
            <>
              <h3 className="text-lg font-medium mb-2">No meetings match your search</h3>
              <p className="text-muted-foreground mb-4">Try adjusting your filters.</p>
              <Button variant="outline" onClick={() => { setSearchText(''); setSearchDate(''); }}>
                Clear filters
              </Button>
            </>
          ) : (
            <>
              <h3 className="text-lg font-medium mb-2">{t('attendance.noMeetings')}</h3>
              <p className="text-muted-foreground mb-4">{t('attendance.createFirst')}</p>
              <Button onClick={() => setIsAddDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('attendance.createMeeting')}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Create Meeting Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('attendance.createMeeting')}</DialogTitle>
            <DialogDescription>
              Create a new meeting to track attendance
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateMeeting} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="meetingDate">{t('attendance.meetingDate')}</Label>
              <Input
                id="meetingDate"
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="meetingType">{t('attendance.meetingType')}</Label>
              <Select value={meetingType} onValueChange={(v) => setMeetingType(v as 'Regular' | 'Special' | 'Event')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Regular">{t('attendance.regular')}</SelectItem>
                  <SelectItem value="Special">{t('attendance.special')}</SelectItem>
                  <SelectItem value="Event">{t('attendance.event')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="openingCash">{t('attendance.openingCash')}</Label>
              <Input
                id="openingCash"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={openingCash}
                onChange={(e) => setOpeningCash(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">{t('attendance.notes')} (Optional)</Label>
              <Input
                id="notes"
                placeholder="Any additional notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setIsAddDialogOpen(false)}
              >
                {t('app.cancel')}
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  t('app.create')
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
